import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articlesData } from "./articlesData";

export default function ArticlePage({ articleId }: { articleId: string }) {
  const { lang = "en" } = useParams<{ lang?: string }>();
  const { t } = useTranslation();
  const article = articlesData[articleId] || articlesData["spreadsheet"];

  const getPath = (page: string) => `/${lang}/${page}`;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Breadcrumb */}
      <nav className="container-limit mx-auto px-4 md:px-6 lg:px-8 pt-6 pb-2" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm text-foreground-500">
          <li>
            <Link to={getPath("")} className="hover:text-foreground-800 transition-colors">
              {t("article.home")}
            </Link>
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </li>
          <li className="text-foreground-700 font-medium">{article.h1}</li>
        </ol>
      </nav>

      <article className="container-limit mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 max-w-4xl">
        {/* Meta info */}
        <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {t("article.lastUpdated")}: {article.lastUpdated}
          </span>
          <span>|</span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {article.author}
          </span>
        </div>

        {/* H1 */}
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 leading-tight">
          {article.h1}
        </h1>

        {/* Introduction */}
        <p className="text-lg md:text-xl text-foreground-700 leading-relaxed mb-10">
          {article.intro}
        </p>

        {/* Disclaimer */}
        <div className="bg-secondary-50 border border-secondary-200/50 rounded-lg p-4 mb-10">
          <p className="text-sm text-secondary-700">
            <strong>{t("article.disclaimer")}:</strong> {t("article.disclaimerText")}
          </p>
        </div>

        {/* Sections */}
        {article.sections.map((section, idx) => (
          <section key={idx} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              {section.h2}
            </h2>
            {section.content && (
              <p className="text-base text-foreground-700 leading-relaxed mb-4">
                {section.content}
              </p>
            )}
            {section.h3s && (
              <div className="space-y-4 mt-4">
                {section.h3s.map((h3, h3Idx) => (
                  <div key={h3Idx}>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      {h3.h3}
                    </h3>
                    <p className="text-base text-foreground-700 leading-relaxed">
                      {h3.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {section.list && (
              <ul className="mt-4 space-y-2">
                {section.list.map((item, liIdx) => (
                  <li key={liIdx} className="flex items-start gap-2 text-base text-foreground-700">
                    <i className="ri-check-line text-accent-500 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {section.table && (
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-background-100">
                      {section.table.headers.map((h, hi) => (
                        <th key={hi} className="text-left px-4 py-3 font-semibold text-foreground-900">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map((row, ri) => (
                      <tr key={ri} className="border-t border-background-200/50">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-4 py-3 text-foreground-700">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        {/* FAQ Section */}
        <section className="mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            {t("article.faqTitle")}
          </h2>
          <div className="space-y-4">
            {article.faqs.map((faq, fidx) => (
              <div key={fidx} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                <h3 className="font-semibold text-foreground-900 mb-2 flex items-start gap-2">
                  <i className="ri-question-line text-primary-500 mt-0.5 shrink-0" />
                  {faq.q}
                </h3>
                <p className="text-base text-foreground-700 leading-relaxed pl-6">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="bg-primary-50 rounded-lg p-6 md:p-8 text-center mb-10">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
            {t("article.readyToExplore")}
          </h3>
          <p className="text-sm text-foreground-600 mb-4">
            {t("article.ctaDesc")}
          </p>
          <a
            href={article.ctaLink}
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary inline-flex"
          >
            <i className="ri-external-link-line mr-2" />
            {t("article.ctaText")}
          </a>
        </div>

        {/* Internal Links */}
        <section className="mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
            {t("article.relatedArticles")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {article.relatedArticles.map((rel, ridx) => (
              <Link
                key={ridx}
                to={getPath(rel.path)}
                className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-colors"
              >
                <i className="ri-article-line text-primary-500 text-lg" />
                <span className="text-sm font-medium text-foreground-800 hover:text-primary-700 transition-colors">
                  {rel.title}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Back to home */}
        <div className="text-center">
          <Link
            to={getPath("")}
            className="inline-flex items-center text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2" />
            {t("article.backToHome")}
          </Link>
        </div>
      </article>
    </div>
  );
}